import type React from 'react';
import { INPUT_TYPE, operatorMap, CONDITION_DELIMITER } from './constants';
import type { Answer, Questionnaire, EnhancedQuestion, Error, ConditionsType } from './types';
export const createQuestionsMap = (questionnaire: Questionnaire): Map<string, EnhancedQuestion> => {
  const questionsMap = new Map<string, EnhancedQuestion>();
  questionnaire.questionList.forEach(question => {
    questionsMap.set(question.id, {
      ...question,
      followUpQuestions: [],
      errors: []
    });
  });
  questionnaire.followUpList.forEach(followUp => {
    const parentQuestion = questionsMap.get(followUp.questionId);
    if (parentQuestion) {
      for (let questionId of followUp.questionIdList) {
        if (questionId && questionsMap.has(questionId)) {
          parentQuestion.followUpQuestions.push({
            questionId,
            conditionList: followUp.conditionList
          });
        }
      }
    }
  });
  return questionsMap;
};
export const createAnswersMap = (questionnaireAnswers: Answer[]): Map<string, Answer> => {
  return new Map(questionnaireAnswers.map(questionnaireAnswer => [questionnaireAnswer.questionId, questionnaireAnswer]));
};
export const isRadio = (tags: string[]): boolean => {
  return tags && tags.some((tag: string) => tag.toLowerCase() === INPUT_TYPE.RADIO);
};
export const getPlaceholder = (tags: string[]): string | undefined => {
  const placeholderTag = tags.find((tag: string) => /^placeholder\s?:/i.test(tag));
  if (placeholderTag) {
    return placeholderTag.split(':').slice(1).join(':').trim();
  }
  return undefined;
};

/**
 * Creates a new map of questions with their `errors` field populated from a list of
 * error records. For each question in `questionsMap`, the returned map will contain
 * the same question data but with `errors` replaced by the matching entry from `errors`
 * (or an empty array if no matching error is found).
 *
 * This function does not mutate the input map or questions; it returns a new map with
 * new question objects to preserve referential integrity.
 *
 * @param questionsMap Map keyed by `question.id` that provides the base questions.
 * @param errors Array of error objects keyed by `questionId` whose `errors` will be applied.
 * @returns A new Map with `errors` hydrated per question.
 */
export const createQuestionsMapWithErrors = (questionsMap: Map<string, EnhancedQuestion>, errors: Error[]): Map<string, EnhancedQuestion> => {
  const errorsMap = new Map<string, Error>();
  errors.forEach((error: Error) => {
    errorsMap.set(error.questionId, error);
  });
  return new Map(Array.from(questionsMap.values()).map(question => [question.id, {
    ...question,
    errors: errorsMap.get(question.id)?.errors ?? []
  }]));
};

/**
 * Returns the first error message for a question, if present.
 *
 * @param question The question whose error messages should be inspected.
 * @returns The first error string, or `undefined` if there are no errors.
 */
export const getErrorMessageFromQuestion = (question: EnhancedQuestion): React.ReactNode | undefined => {
  return !!question.errors?.length ? question.errors[0] : undefined;
};

/**
 * Get selected answers from question's answerOptions that match userAnswers
 */
const getSelectedAnswers = (question: EnhancedQuestion, userAnswers: string[]): Array<{
  answer: string;
  metadata: Record<string, string>;
}> => {
  if (!question.answerOptions || !userAnswers) return [];
  return question.answerOptions.filter(option => userAnswers.includes(option.answer));
};
const getKeyValue = (answer: string): [string, string] => {
  const parts = answer.split(CONDITION_DELIMITER);
  return [parts[0] || '', parts[1] || ''];
};
const getValueFromPath = (answer: {
  metadata: Record<string, string>;
}, metaKey: string): string => {
  const keys = metaKey.split('.');
  let value: any = answer.metadata;
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) break;
  }
  return value || '';
};

/**
 * Check if an answer matches the condition (ported from old library)
 */
const getSelectedAnswer = (userAnswers: string[], answer: string, operator: string | undefined, question: EnhancedQuestion): boolean => {
  if (!answer) {
    return false;
  }
  const normalizedOperator = operator || operatorMap.equals;

  // Handle metadata operators
  if (normalizedOperator === operatorMap.metadata_not_equals || normalizedOperator === operatorMap.metadata_includes || normalizedOperator === operatorMap.metadata_not_includes || normalizedOperator === operatorMap.metadata_equals) {
    if (answer && !answer.includes(CONDITION_DELIMITER)) {
      //console.warn('condition value does not contain delimiter', CONDITION_DELIMITER);
      return false;
    }
  }
  switch (normalizedOperator) {
    case operatorMap.equals:
      return userAnswers.includes(answer);
    case operatorMap.not_equals:
      return !userAnswers.some(userAnswer => userAnswer === answer);
    case operatorMap.includes:
      return userAnswers.some(userAnswer => typeof userAnswer === 'string' && userAnswer.includes(answer));
    case operatorMap.not_includes:
      return !userAnswers.some(userAnswer => typeof userAnswer === 'string' && userAnswer.includes(answer));
    case operatorMap.metadata_equals:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey);
          return answerMetaValue === metaValue;
        });
        return matched;
      }
    case operatorMap.metadata_not_equals:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey);
          return answerMetaValue === metaValue;
        });
        return !matched;
      }
    case operatorMap.metadata_includes:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey) || '';
          return String(answerMetaValue).includes(metaValue);
        });
        return matched;
      }
    case operatorMap.metadata_not_includes:
      {
        const selectedOriginalAnswers = getSelectedAnswers(question, userAnswers);
        const [metaKey, metaValue] = getKeyValue(answer);
        const matched = selectedOriginalAnswers.some(originalAnswer => {
          const answerMetaValue = getValueFromPath(originalAnswer, metaKey) || '';
          return String(answerMetaValue).includes(metaValue);
        });
        return !matched;
      }
  }
  return false;
};

/**
 * Transform backend condition format to old format expected by matchConditionWithAnswers
 * Backend format: conditionList -> conditionList -> equationList -> {type, questionId, answer}
 * Old format: [[{id, answer, operator}]]
 */
const transformConditionList = (conditionList: any): ConditionsType => {
  if (!conditionList || !Array.isArray(conditionList)) {
    return [];
  }
  return conditionList.map((outerCondition: any) => {
    if (!outerCondition.conditionList || !Array.isArray(outerCondition.conditionList)) {
      return [];
    }
    return outerCondition.conditionList.flatMap((innerCondition: any) => {
      if (!innerCondition.equationList || !Array.isArray(innerCondition.equationList)) {
        return [];
      }
      return innerCondition.equationList.map((equation: any) => ({
        id: equation.questionId || equation.id || '',
        answer: equation.answer || '',
        operator: equation.type || operatorMap.equals
      }));
    });
  });
};

/**
 * Match conditions against current answers (ported from old library)
 */
const matchConditionWithAnswers = (conditions: ConditionsType, userAnswersMap: Map<string, Answer>, question?: EnhancedQuestion): boolean => {
  // No equations to evaluate — treat as satisfied (e.g. follow-up with conditionList: [])
  if (!conditions || conditions.length === 0) {
    return true;
  }
  let matched = false;
  // OR - any group matching returns true
  for (const ANDs of conditions) {
    // AND - all conditions in group must match
    let resultForAnd = true;
    for (const AND of ANDs) {
      const answerData = userAnswersMap.get(AND.id);
      const answersToQuestion = answerData?.answers || [];
      if (answersToQuestion.length === 0 || !getSelectedAnswer(answersToQuestion, AND.answer, AND.operator, question!)) {
        resultForAnd = false;
      }
      if (!resultForAnd) {
        break;
      }
    }
    if (resultForAnd) {
      matched = true;
      break;
    }
  }
  return matched;
};

/**
 * Get visible follow-up question IDs based on current answers
 */
export const getVisibleFollowUpQuestionIds = (followUpQuestions: Array<{
  questionId: string;
  conditionList: any;
}>, questionnaireAnswers: Map<string, Answer>, parentQuestion: EnhancedQuestion): string[] => {
  if (!followUpQuestions || followUpQuestions.length === 0) {
    return [];
  }
  return followUpQuestions.filter(followUp => {
    const transformedConditions = transformConditionList(followUp.conditionList);
    return matchConditionWithAnswers(transformedConditions, questionnaireAnswers, parentQuestion);
  }).map(followUp => followUp.questionId);
};